import { supabase } from "@/integrations/supabase/client";


export type MatchMode = "contains" | "exact" | "prefix" | "suffix";

export type ReservationRow = {
  username: string;
  reason: string;
  reserved_until: string | null;
  allocated_to_user_id: string | null;
  allocated_to_workspace_id: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
};

export type BlockRow = {
  id: string;
  pattern: string;
  match_mode: MatchMode;
  note: string | null;
  created_by: string | null;
  created_at: string;
};

export type ReservationListResponse = {
  rows: ReservationRow[];
  user_labels: Record<string, { email: string | null; display_name: string | null }>;
  workspace_labels: Record<string, string | null>;
};

export type BlockListResponse = {
  rows: BlockRow[];
  user_labels: Record<string, { email: string | null }>;
};

const SEEDED_REASONS = new Set(["system", "route", "brand", "former_handle", "admin_rename_former"]);
export function isSeededReservation(r: ReservationRow): boolean {
  return SEEDED_REASONS.has(r.reason);
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-manage-handle-controls", { body });
  if (error) throw new Error(error.message);
  const payload = data as { error?: string } & T;
  if (payload?.error) throw new Error(payload.error);
  return payload;
}

export const handleControls = {
  reservation: {
    list: (filters: {
      search?: string;
      reason?: string | null;
      allocated_only?: boolean;
      expiring_within_days?: number | null;
    } = {}) =>
      call<ReservationListResponse>({ action: "reservation.list", ...filters }),
    create: (input: {
      username: string; reason?: string; reserved_until?: string | null;
      allocated_to_user_id?: string | null; allocated_to_workspace_id?: string | null; note?: string | null;
    }) => call<{ ok: true }>({ action: "reservation.create", ...input }),
    update: (input: {
      original_username: string;
      username: string; reason?: string; reserved_until?: string | null;
      allocated_to_user_id?: string | null; allocated_to_workspace_id?: string | null; note?: string | null;
    }) => call<{ ok: true }>({ action: "reservation.update", ...input }),
    release: (username: string) =>
      call<{ ok: true }>({ action: "reservation.release", username }),
  },
  block: {
    list: (filters: { search?: string } = {}) =>
      call<BlockListResponse>({ action: "block.list", ...filters }),
    create: (input: { pattern: string; match_mode?: MatchMode; note?: string | null }) =>
      call<{ ok: true }>({ action: "block.create", ...input }),
    update: (input: { id: string; pattern: string; match_mode?: MatchMode; note?: string | null }) =>
      call<{ ok: true }>({ action: "block.update", ...input }),
    release: (id: string) => call<{ ok: true }>({ action: "block.release", id }),
  },
};

/**
 * Mirror the server-side block-matching logic so the UI can preview a candidate
 * handle without a round-trip. Returns the first matching pattern, or null.
 */
export function previewHandleBlock(handle: string, blocks: BlockRow[]): BlockRow | null {
  const h = handle.trim().toLowerCase();
  if (!h) return null;
  for (const b of [...blocks].sort((a, z) => z.pattern.length - a.pattern.length)) {
    const p = b.pattern.toLowerCase();
    let hit = false;
    if (b.match_mode === "exact") hit = h === p;
    else if (b.match_mode === "prefix") hit = h.startsWith(p);
    else if (b.match_mode === "suffix") hit = h.endsWith(p);
    else hit = h.includes(p);
    if (hit) return b;
  }
  return null;
}
